// ============================================================
// GitHooks.jsx
//
// A branded, responsive course content page for the
// "Git Hooks & Automation" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Local hook architecture, shell script examples,
//           and Husky integration for team-wide automation.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AnchorIcon             from "@mui/icons-material/Anchor";
import PsychologyIcon         from "@mui/icons-material/Psychology";
import BoltIcon               from "@mui/icons-material/Bolt";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import SettingsSuggestIcon    from "@mui/icons-material/SettingsSuggest";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitHooks() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<BoltIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Workflow Automation"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git <span>Hooks & Automation</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Automate your quality control. Git hooks are event-driven scripts that trigger at key points in your workflow—allowing you to enforce linting, run tests, and validate commit messages automatically.
                    </Typography>
                </Box>

                {/* 1. HOOKS ARCHITECTURE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PsychologyIcon />} label="1. The Internal Trigger System" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Hooks reside inside your local repository within the hidden <code>.git/hooks/</code> directory. They are shell scripts that return an exit code of 0 for success or 1 to abort the Git action.
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { name: "pre-commit", desc: "Runs before the commit is created. Best for linting and formatting checks." },
                            { name: "commit-msg", desc: "Verifies the commit message follows team standards (e.g., JIRA-123 prefixes)." },
                            { name: "pre-push", desc: "Runs before uploading to remote. Ideal for running the full test suite." },
                            { name: "post-merge", desc: "Triggers after a merge. Useful for re-installing dependencies automatically." }
                        ].map((hook, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 13, mb: 0.5 }}>{hook.name}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{hook.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 2. MANUAL IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="2. Scripting a pre-commit Guard" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        To activate a hook manually, remove the <code>.sample</code> extension and make the file executable.
                    </Typography>
                    <CodeBlock
                        title=".git/hooks/pre-commit"
                        code={`#!/bin/sh\n\nnpm run lint\n\nif [ $? -ne 0 ]; then\n  echo "❌ Error: Lint failed. Fix code before committing."\n  exit 1\nfi\n\n# Terminal: chmod +x .git/hooks/pre-commit`}
                    />
                </Box>

                {/* 3. TEAM AUTOMATION (HUSKY) */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsSuggestIcon />} label="3. Professional Setup with Husky" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Since the <code>.git</code> folder isn't pushed to GitHub, you need <strong>Husky</strong> to share hooks with your team via <code>package.json</code>.
                    </Typography>
                    <CodeBlock
                        title="Husky Integration Flow"
                        code={`# Install Husky\nnpm install husky --save-dev\n\n# Initialize\nnpx husky install\n\n# Add a hook programmatically\nnpx husky add .husky/pre-commit "npm test"`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Automation Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Keep pre-commit hooks fast; they run every time you commit.",
                            "Use hooks to prevent leaking secrets/API keys to the repository.",
                            "Prefer shared tools like Husky over manual .git/hooks for team projects.",
                            "Allow an 'escape hatch' using --no-verify for emergency commits.",
                            "Automate 'npm install' via post-merge if package.json changes.",
                            "Use commit-msg hooks to enforce Conventional Commits standards."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Need to bypass a failing hook for a quick experiment? Use <code>git commit -m "msg" --no-verify</code>. Use this sparingly, as it skips your automated safety checks!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
